"use strict";
var __knownSymbol = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __using = (stack, value, async) => {
  if (value != null) {
    if (typeof value !== "object" && typeof value !== "function") __typeError("Object expected");
    var dispose, inner;
    if (async) dispose = value[__knownSymbol("asyncDispose")];
    if (dispose === void 0) {
      dispose = value[__knownSymbol("dispose")];
      if (async) inner = dispose;
    }
    if (typeof dispose !== "function") __typeError("Object not disposable");
    if (inner) dispose = function() {
      try {
        inner.call(this);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    stack.push([async, dispose, value]);
  } else if (async) {
    stack.push([async]);
  }
  return value;
};
var __callDispose = (stack, error, hasError) => {
  var E = typeof SuppressedError === "function" ? SuppressedError : function(e, s, m, _) {
    return _ = Error(m), _.name = "SuppressedError", _.error = e, _.suppressed = s, _;
  };
  var fail = (e) => error = hasError ? new E(e, error, "An error was suppressed during disposal") : (hasError = true, e);
  var next = (it) => {
    while (it = stack.pop()) {
      try {
        var result = it[1] && it[1].call(it[2]);
        if (it[0]) return Promise.resolve(result).then(next, (e) => (fail(e), next()));
      } catch (e) {
        fail(e);
      }
    }
    if (hasError) throw error;
  };
  return next();
};
/**
 * @license
 * Copyright 2020 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { fetchOkWithCredentials } from "#src/credentials_provider/http_request.js";
import {
  CredentialsProvider,
  makeCredentialsGetter
} from "#src/credentials_provider/index.js";
import {
  getCredentialsWithStatus,
  monitorAuthPopupWindow
} from "#src/credentials_provider/interactive_credentials_provider.js";
import { raceWithAbort } from "#src/util/abort.js";
import { fetchOk, HttpError } from "#src/util/http_request.js";
import {
  verifyObject,
  verifyObjectProperty,
  verifyString
} from "#src/util/json.js";
import { ProgressSpan } from "#src/util/progress_listener.js";
function makeOriginError(serverUrl) {
  return new Error(
    `ngauth server ${serverUrl} does not allow requests from Neuroglancer instance ${self.origin}`
  );
}
async function waitForLogin(serverUrl, signal) {
  const abortController = new AbortController();
  signal = AbortSignal.any([abortController.signal, signal]);
  try {
    const newWindow = window.open(
      `${serverUrl}/login?origin=${encodeURIComponent(self.origin)}`
    );
    if (newWindow === null) {
      throw new Error("Failed to create authentication popup window");
    }
    monitorAuthPopupWindow(newWindow, abortController);
    return await raceWithAbort(
      waitForAuthResponseMessage(serverUrl, newWindow, abortController.signal),
      signal
    );
  } finally {
    abortController.abort();
  }
}
function waitForAuthResponseMessage(serverUrl, source, signal) {
  return new Promise((resolve, reject) => {
    window.addEventListener(
      "message",
      (event) => {
        if (event.source !== source) return;
        const eventOrigin = event.origin || event.originalEvent.origin;
        if (eventOrigin !== serverUrl) {
          return;
        }
        const { data } = event;
        if (event.data === "badorigin") {
          reject(makeOriginError(serverUrl));
          return;
        }
        try {
          verifyObject(data);
          const token = verifyObjectProperty(data, "token", verifyString);
          resolve({ token });
        } catch (e) {
          reject(
            new Error(
              `Received unexpected authentication response: ${e.message}`
            )
          );
          console.error(
            "ngauth: Received unexpected message from ${serverUrl}",
            event
          );
        }
      },
      { signal }
    );
  });
}
export class NgauthCredentialsProvider extends CredentialsProvider {
  constructor(serverUrl) {
    super();
    this.serverUrl = serverUrl;
  }
  get = makeCredentialsGetter(async (options) => {
    var _stack = [];
    try {
      const _span = __using(_stack, new ProgressSpan(options.progressListener, {
        message: `Requesting ngauth login token from ${this.serverUrl}`
      }));
      try {
        const response = await fetchOk(`${this.serverUrl}/token`, {
          method: "POST",
          credentials: "include",
          signal: options.signal
        });
        return { token: await response.text() };
      } catch (e) {
        if (e instanceof HttpError) {
          switch (e.status) {
            case 401:
              return await getCredentialsWithStatus(
                {
                  description: `ngauth server ${this.serverUrl}`,
                  requestDescription: "login",
                  get: (signal) => waitForLogin(this.serverUrl, signal)
                },
                options.signal
              );
            case 403:
              throw makeOriginError(this.serverUrl);
          }
        }
        throw e;
      }
    } catch (_) {
      var _error = _, _hasError = true;
    } finally {
      __callDispose(_stack, _error, _hasError);
    }
  });
}
export class NgauthGcsCredentialsProvider extends CredentialsProvider {
  constructor(ngauthCredentialsProvider, serverUrl, bucket) {
    super();
    this.ngauthCredentialsProvider = ngauthCredentialsProvider;
    this.serverUrl = serverUrl;
    this.bucket = bucket;
  }
  get = makeCredentialsGetter(async (options) => {
    var _stack = [];
    try {
      const _span = __using(_stack, new ProgressSpan(options.progressListener, {
        message: `Requesting access token for gs://${this.bucket} from ${this.serverUrl}`
      }));
      const response = await fetchOkWithCredentials(
        this.ngauthCredentialsProvider,
        `${this.serverUrl}/gcs_token`,
        { method: "POST", signal: options.signal },
        (credentials, init) => {
          return {
            ...init,
            body: JSON.stringify({
              token: credentials.token,
              bucket: this.bucket
            })
          };
        },
        (error) => {
          const { status } = error;
          if (status === 401) {
            return "refresh";
          }
          throw error;
        }
      );
      return { tokenType: "Bearer", accessToken: (await response.json()).token };
    } catch (_) {
      var _error = _, _hasError = true;
    } finally {
      __callDispose(_stack, _error, _hasError);
    }
  });
}
